import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Package, DollarSign, Users, ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { formatPrice } from '../../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AdminAdvancedStats = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [productsRes, ordersRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/products?status=`),
        axios.get(`${API_URL}/orders`, { headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` } }),
        axios.get(`${API_URL}/categories`)
      ]);

      const products = productsRes.data;
      const orders = ordersRes.data;
      const categories = categoriesRes.data;

      // Calculate advanced metrics
      const totalRevenue = orders.reduce((sum, order) => sum + order.total_amount, 0);
      const avgOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;
      const totalProductsInStock = products.reduce((sum, p) => sum + p.stock_quantity, 0);
      const lowStockProducts = products.filter(p => p.stock_quantity > 0 && p.stock_quantity <= 20).length;
      const outOfStockProducts = products.filter(p => p.stock_quantity === 0).length;
      const avgRating = products.reduce((sum, p) => sum + (p.rating || 0), 0) / products.length;
      
      // Top category by products
      const categoryStats = categories.map(cat => ({
        name: cat.name,
        count: products.filter(p => p.category_id === cat.id).length
      })).sort((a, b) => b.count - a.count);

      // Recent orders (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentOrders = orders.filter(o => new Date(o.created_at) > sevenDaysAgo);

      setStats({
        totalRevenue,
        avgOrderValue,
        totalProductsInStock,
        lowStockProducts,
        outOfStockProducts,
        avgRating,
        topCategory: categoryStats[0],
        recentOrdersCount: recentOrders.length,
        pendingOrders: orders.filter(o => o.status === 'pending').length,
        completedOrders: orders.filter(o => o.status === 'delivered').length
      });
    } catch (error) {
      console.error('Failed to fetch advanced stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl font-bold">Advanced Statistics</h2>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Average Order Value */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.avgOrderValue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Per order revenue
            </p>
          </CardContent>
        </Card>

        {/* Total Stock */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Stock</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalProductsInStock}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Units in inventory
            </p>
          </CardContent>
        </Card>

        {/* Low Stock Alert */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Low Stock Items</CardTitle>
            <Package className="h-4 w-4 text-warning" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">{stats.lowStockProducts}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Products ≤ 20 units
            </p>
          </CardContent>
        </Card>

        {/* Out of Stock */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Out of Stock</CardTitle>
            <Package className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{stats.outOfStockProducts}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Products unavailable
            </p>
          </CardContent>
        </Card>

        {/* Average Rating */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Product Rating</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.avgRating.toFixed(1)}★</div>
            <p className="text-xs text-muted-foreground mt-1">
              Customer satisfaction
            </p>
          </CardContent>
        </Card>

        {/* Top Category */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Category</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">{stats.topCategory?.name}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.topCategory?.count} products
            </p>
          </CardContent>
        </Card>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Orders (Last 7 Days)</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.recentOrdersCount}</div>
            <p className="text-xs text-success mt-1">
              {stats.pendingOrders} pending
            </p>
          </CardContent>
        </Card>

        {/* Completion Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.completedOrders > 0 ? Math.round((stats.completedOrders / (stats.completedOrders + stats.pendingOrders)) * 100) : 0}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.completedOrders} delivered
            </p>
          </CardContent>
        </Card>

        {/* Total Revenue */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{formatPrice(stats.totalRevenue)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              All time earnings
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};